import { type ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import GroupIcon from '@mui/icons-material/Group';
import HistoryIcon from '@mui/icons-material/History';
import SecurityIcon from '@mui/icons-material/Security';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

interface AuthShellProps {
  children: ReactNode;
}

// Two-column auth layout: brand panel on the left (≥md), form on the right.
export default function AuthShell({ children }: AuthShellProps) {
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: 'background.default' }}>
      {/* Brand panel */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          p: 6,
          pr: { md: 6, lg: 10 },
          color: '#fff',
          backgroundImage: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 50%, #6366f1 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 36, height: 36, borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.16)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <SecurityIcon sx={{ fontSize: 22 }} />
          </Box>
          <Typography
            component="span"
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: 22,
              fontWeight: 700,
              lineHeight: 1,
              letterSpacing: '-0.01em',
            }}
          >
            {t('app.name')}
          </Typography>
        </Box>

        <Box sx={{ maxWidth: 440 }}>
          <Typography
            variant="h3"
            sx={{ fontWeight: 700, lineHeight: 1.15, mb: 2, fontSize: { xs: '2.25rem', md: '3rem' } }}
          >
            {t('auth.brand.headline')}
          </Typography>
          <Typography sx={{ opacity: 0.85, mb: 4, fontSize: '1.05rem' }}>
            {t('auth.brand.subhead')}
          </Typography>
          <Stack spacing={2}>
            <Feature icon={<LockIcon />} text={t('auth.brand.feature1')} />
            <Feature icon={<GroupIcon />} text={t('auth.brand.feature2')} />
            <Feature icon={<HistoryIcon />} text={t('auth.brand.feature3')} />
          </Stack>
        </Box>

        <Typography variant="caption" sx={{ opacity: 0.6 }}>
          © {new Date().getFullYear()} {t('app.name')}
        </Typography>
      </Box>

      {/* Form panel */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'center', md: 'flex-start' },
          p: { xs: 2, sm: 4 },
          pl: { md: 8, lg: 12 },
          position: 'relative',
        }}
      >
        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          <LanguageSwitcher color="primary" />
        </Box>
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          {/* Mobile-only wordmark */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: 'primary.main', color: 'primary.contrastText',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <SecurityIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography
              component="span"
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: 22,
                fontWeight: 700,
                lineHeight: 1,
                color: 'primary.main',
              }}
            >
              {t('app.name')}
            </Typography>
          </Box>
          {children}
        </Box>
      </Box>
    </Box>
  );
}

function Feature({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box
        sx={{
          width: 32, height: 32, borderRadius: 1.5,
          bgcolor: 'rgba(255,255,255,0.16)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ opacity: 0.92, fontSize: '1rem' }}>{text}</Typography>
    </Box>
  );
}
